import { getSurveyBySlug as apiGetSurveyBySlug, ApiError } from './apiService';

/**
 * Encuestas de tipo fijo consultadas por `slug` (`GET /v1/surveys/:slug`),
 * usadas por formularios como "Registro de vivienda". Su esquema de
 * preguntas es distinto al de `surveyService.js` (preguntas
 * identificadas por `key` en vez de `order`, condiciones referenciando
 * `question_key`, tipos `boolean`/`radio`/`textarea`), por eso se
 * normalizan por separado en vez de reusar `normalizeQuestion`.
 *
 * Igual que `surveyService.js`, cachea en localStorage por `slug` y
 * cae a la última copia guardada si no hay conexión.
 */

const DETAIL_CACHE_PREFIX = 'pulso17.dynamicForms.cache.detail.';

function isOffline(err) {
  return err instanceof ApiError && err.status === 0;
}

function readCache(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeCache(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // localStorage lleno o no disponible: seguimos sin caché.
  }
}

function cacheDetail(slug, survey) {
  writeCache(`${DETAIL_CACHE_PREFIX}${slug}`, survey);
}

function getCachedDetail(slug) {
  return readCache(`${DETAIL_CACHE_PREFIX}${slug}`);
}

/**
 * Opción de una pregunta `radio`.
 * @typedef {Object} DynamicOption
 * @property {string} id
 * @property {string} value
 * @property {string} label
 * @property {number} order
 * @property {Object} metadata datos libres por opción (ej. `requires_specification`, `score`)
 */
function normalizeOption(option) {
  return {
    id: String(option.id),
    value: option.value,
    label: option.label,
    order: option.order,
    metadata: option.metadata || {},
  };
}

/**
 * Condición de visibilidad de una pregunta, referenciando la `key`
 * de otra pregunta (no su `order`, a diferencia de `surveyService.js`).
 * @typedef {Object} DynamicCondition
 * @property {string} questionKey
 * @property {'equals'} operator
 * @property {*} value
 */
function normalizeCondition(condition) {
  return {
    questionKey: condition.question_key,
    operator: condition.operator,
    value: condition.value,
  };
}

/**
 * Pregunta ya normalizada de un formulario dinámico por slug.
 * @typedef {Object} DynamicQuestion
 * @property {string} id
 * @property {string} key
 * @property {string} label
 * @property {'boolean'|'radio'|'textarea'} type
 * @property {number} order
 * @property {boolean} required
 * @property {Object} validation ej. `{ max: 500 }` para `textarea`
 * @property {Object} settings ej. `{ true_label, false_label }` o `{ rows, placeholder }`
 * @property {DynamicCondition[]} conditions
 * @property {DynamicOption[]} options
 */
function normalizeValidation(validation) {
  if (Array.isArray(validation) || !validation) return {};
  return {
    max: validation.max ?? null,
    afterOrEqual: validation.after_or_equal ?? null,
    minDigits: validation.min_digits ?? null,
    maxDigits: validation.max_digits ?? null,
    email: validation.email ?? null,
  };
}

function normalizeQuestion(question) {
  return {
    id: String(question.id),
    key: question.key,
    label: question.label,
    type: question.type,
    order: question.order,
    required: Boolean(question.required),
    validation: normalizeValidation(question.validation),
    settings: Array.isArray(question.settings) ? {} : question.settings || {},
    conditions: Array.isArray(question.conditions) ? question.conditions.map(normalizeCondition) : [],
    options: Array.isArray(question.options)
      ? [...question.options].sort((a, b) => a.order - b.order).map(normalizeOption)
      : [],
  };
}

/**
 * Formulario dinámico normalizado.
 * @typedef {Object} DynamicSurvey
 * @property {string} id
 * @property {string} slug
 * @property {string} label
 * @property {string} description
 * @property {string} updatedAt
 * @property {DynamicQuestion[]} questions
 */
function normalizeSurvey(survey) {
  const questions = Array.isArray(survey.questions)
    ? [...survey.questions].sort((a, b) => a.order - b.order).map(normalizeQuestion)
    : [];
  return {
    id: String(survey.id),
    slug: survey.slug,
    label: survey.label,
    description: survey.description,
    updatedAt: survey.updated_at,
    questions,
  };
}

/**
 * Obtiene un formulario dinámico por `slug`, con sus preguntas. Cae a
 * la última copia guardada localmente si no hay conexión. Devuelve
 * `null` si la API responde 404.
 *
 * @param {string} slug
 * @returns {Promise<DynamicSurvey|null>}
 */
export async function getSurveyBySlug(slug) {
  try {
    const response = await apiGetSurveyBySlug(slug);
    if (!response?.data) return null;
    const survey = normalizeSurvey(response.data);
    cacheDetail(slug, survey);
    return survey;
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    if (isOffline(err)) {
      const cached = getCachedDetail(slug);
      if (cached) return cached;
    }
    throw err;
  }
}

/** `true` si hay una copia guardada localmente para este `slug`. */
export function hasCachedForm(slug) {
  return getCachedDetail(slug) != null;
}
